import { applyNodeChanges, Edge, Node, NodeChange } from '@xyflow/react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import {
  hydrateReactFlowElements,
  transformToReactFlow,
} from '@/sections/insights/queryBuilder/components/QueryVisualizer/utils';
import type { FlatGraph } from '@/sections/insights/queryBuilder/types/FlatGraph';
import { QueryAction } from '@/sections/insights/queryBuilder/types/QueryActions';

interface UseReactFlowStateProps {
  flatGraph: FlatGraph | undefined;
  dispatch: React.Dispatch<QueryAction>;
  editable: boolean;
  isPlacementMode: boolean;
}

interface UseReactFlowStateReturn {
  rfNodes: Node[];
  rfEdges: Edge[];
  expandedModalIds: Set<string>;
  cursorPosition: { x: number; y: number } | null;
  onNodesChange: (changes: NodeChange[]) => void;
  toggleModalExpanded: (id: string) => void;
  setCursorPosition: React.Dispatch<
    React.SetStateAction<{ x: number; y: number } | null>
  >;
  setRfNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setRfEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
}

/**
 * useReactFlowState - Manages ReactFlow state and provides callbacks
 *
 * Handles:
 * - Local ReactFlow nodes/edges state
 * - Modal expansion state
 * - Cursor position tracking for placement mode
 * - Node position change handling with dispatch
 * - Hydration of ReactFlow elements from FlatGraph
 */
export const useReactFlowState = ({
  flatGraph,
  dispatch,
  editable,
  isPlacementMode,
}: UseReactFlowStateProps): UseReactFlowStateReturn => {
  // Modal expansion state
  const [expandedModalIds, setExpandedModalIds] = useState<Set<string>>(
    new Set()
  );

  const toggleModalExpanded = useCallback((id: string) => {
    setExpandedModalIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Cursor position tracking for ghost node
  const [cursorPosition, setCursorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Clear cursor position when placement mode is disabled
  useEffect(() => {
    if (!isPlacementMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Legitimate use case: resetting cursor position when exiting placement mode
      setCursorPosition(null);
    }
  }, [isPlacementMode]);

  // Local state for ReactFlow nodes/edges
  const [rfNodes, setRfNodes] = useState<Node[]>([]);
  const [rfEdges, setRfEdges] = useState<Edge[]>([]);

  // Sync local ReactFlow graph data changes (drag, add, delete) WITHOUT layout
  // Using useMemo to derive state instead of useEffect to avoid cascading renders
  const { hydratedNodes, hydratedEdges } = useMemo(() => {
    const { nodes: rawNodes, edges: rawEdges } =
      transformToReactFlow(flatGraph);
    const { nodes: hydratedNodes, edges: hydratedEdges } =
      hydrateReactFlowElements(
        rawNodes,
        rawEdges,
        editable,
        expandedModalIds,
        toggleModalExpanded,
        flatGraph?.rootId
      );

    return { hydratedNodes, hydratedEdges };
  }, [flatGraph, editable, expandedModalIds, toggleModalExpanded]);

  // Update local state when derived values change
  // useLayoutEffect runs synchronously before paint, avoiding visual flicker
  // This is appropriate here because we're syncing React state with derived props
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Legitimate use case: syncing controlled component state with derived values
    setRfNodes(hydratedNodes);
    setRfEdges(hydratedEdges);
  }, [hydratedNodes, hydratedEdges]);

  // Handle node positioning changes with dispatch
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setRfNodes(nds => {
        const updatedNodes = applyNodeChanges(changes, nds);

        // Persist position changes via dispatch
        changes.forEach(change => {
          if (
            change.type === 'position' &&
            change.position &&
            !change.dragging
          ) {
            dispatch({
              type: 'UPDATE_NODE_POSITION',
              nodeId: change.id,
              position: change.position,
            });
          }
        });

        return updatedNodes;
      });
    },
    [dispatch]
  );

  return {
    rfNodes,
    rfEdges,
    expandedModalIds,
    cursorPosition,
    onNodesChange,
    toggleModalExpanded,
    setCursorPosition,
    setRfNodes,
    setRfEdges,
  };
};
